<template>
    <!--@formatter:off-->
    <li :class="{selected: hasAnswer, finished: isFullAnswers || isDisabled}" @click="checkTryAnswerWhenAnswerStateIsDenyAndValidateEnrolmentPeriod">
        <h4 class="class_name">{{program.programName}}</h4>
        <ul class="bx_num_people">
            <li><span class="num_people">{{$t('EnrolmentResponseProgram_targetGrades', {startGrade, endGrade})}}</span></li>

            <li v-if="isRegularAnswer"><span class="num_people complete">{{$t('common_completeApplyInLimit')}}</span></li>
            <li v-else-if="isReservedAnswer"><span class="num_people complete">{{$t('common_completeApplyReserved', {order: program.answer && program.answer.order})}}</span></li>
            <li v-else-if="isEnrolmentTypeRandom && hasAnswer"><span class="num_people complete"><strong>{{$t('EnrolmentResponseProgram_completeApplySchool')}}</strong> ({{$t('EnrolmentResponseProgram_randomInfo')}}) </span></li>
            <li v-else-if="isEnrolmentTypeRandom && !hasAnswer"><span class="num_people">{{$t('EnrolmentResponseProgram_currentApplyPersons', {number: program.answerRegularCount || 0})}}</span></li>
            <li v-else-if="isEnrolmentTypeFCFS && !isRegularAnswer && !isReservedAnswer">
                <span class="num_people has_bar">{{$t('common_limitInfo', {count: program.answerRegularCount, limit: program.limit})}}</span>
                <span class="num_people2">{{$t('common_reservedInfo', {count: program.answerReservedCount, limit: program.reservedLimit})}}</span>
            </li>
        </ul>
        <div class="desc_wrap">
          <span class="desc_text">{{program.programDesc}}</span>
        </div>
        <span class="bx_btn" v-if="isFullAnswers"><span class="txt_finished">{{$t('EnrolmentResponseProgram_endedProgram')}}</span></span>
        <span class="bx_btn" v-else-if="!isDisabled">
            <button type="button" class="btn_st6 btn_class_register" v-show="showLoadingButton" :disabled="answerStateType === answerStateTypeDeny">
                {{$t('EnrolmentResponseProgram_applying')}}<img :src="loadingSvgPath" :alt="$t('EnrolmentResponseProgram_loading')">
            </button>
            <button type="button" class="btn_st6 btn_class_register cancel" @click="cancelAnswer" v-show="showCancelButton" :disabled="answerStateType === answerStateTypeDeny">{{$t('EnrolmentResponseProgram_cancel')}}</button>
            <button type="button" class="btn_st6 btn_class_register" @click="applyAnswer" v-show="showApplyButton" :disabled="answerStateType === answerStateTypeDeny">{{$t('EnrolmentResponseProgram_apply')}}</button>
        </span>
    </li>
    <!--@formatter:on-->
</template>

<script>
import {ENROLMENT_TYPE} from '@/common/consts/survey.js';
import {ANSWER_TYPE, SURVEY_ANSWER_STATE_TYPE} from '@/common/consts/response.js';
import {getAssetInfo, PAGE_TYPE} from '@/common/consts/service.js';
import {FILTER_TYPE} from '@/common/consts/filter.js';
import {toDateFromISOFormat, toResponseViewFormat} from '@/common/utils/date.js';
import checkTryAnswerWhenAnswerStateIsDeny from '@/common/mixins/checkTryAnswerWhenAnswerStateIsDeny.js';
import {showToast} from '@/common/utils/simpleToast.js';
import {getFilterLabel} from '@/common/utils/filter.js';
import {responseModal} from '@/common/directives/responseModal.js';

import {
    APPLY_ANSWER,
    CANCEL_ANSWER,
    FORCE_CLOSE,
    IS_AFTER_ENROLMENT_PERIOD,
    IS_AVAILABLE_ENROLMENT_PERIOD,
} from '@/common/store/modules/enrolment/response.js';
import {createNamespacedHelpers} from 'vuex';
import {ENROLMENT_RESPONSE_NAMESPACE} from '@/common/store';

const {mapState, mapGetters, mapActions} = createNamespacedHelpers(ENROLMENT_RESPONSE_NAMESPACE);

export default {
    name: 'EnrolmentResponseProgram',
    directives: {responseModal},
    data() {
        return {
            answerStateTypeDeny: SURVEY_ANSWER_STATE_TYPE.DENY,
            loadingSvgPath: null,
        };
    },
    props: {
        program: {
            type: Object,
            required: true,
        },
        questionNo: {
            type: String,
            required: true,
        },
        answerIndex: {
            type: Number,
            required: true,
        },
    },

    mixins: [
        checkTryAnswerWhenAnswerStateIsDeny,
    ],

    computed: {
        ...mapState({
            startDate: state => state.survey.startDate,
            startDateString: state => toResponseViewFormat(state.survey.startDate),
            endDate: state => state.survey.endDate,
            answerStateType: state => state.survey.answerStateType,
            enrolmentType: state => state.survey.enrolmentType,
            questions: state => state.survey.contents.questions,
            filterInfo: state => state.survey.contents.filterInfo,
            isPreview: state => state.options.isPreview,
        }),

        ...mapGetters({
            isAfterEnrolmentPeriod: IS_AFTER_ENROLMENT_PERIOD,
            isAvailableEnrolmentPeriod: IS_AVAILABLE_ENROLMENT_PERIOD,
        }),

        isWebView() {
            return this.$route.query.hasOwnProperty('isMasking');
        },

        /**
         * 수신자가 해당 프로그램에 대해 답변(신청)했는지 여부
         */
        hasAnswer() {
            return !!this.program.answer;
        },

        isEnrolmentTypeFCFS() {
            return this.enrolmentType === ENROLMENT_TYPE.FCFS;
        },

        isEnrolmentTypeRandom() {
            return this.enrolmentType === ENROLMENT_TYPE.RANDOM;
        },

        /**
         * 해당 프로그램 정원(예비포함)이 모두 채워졌는지 여부
         */
        isFullAnswers() {
            return !this.hasAnswer && (this.program.limit + this.program.reservedLimit <= this.program.answerTotalCount);
        },

        /**
         * 수신자가 정원내 신청한 경우
         */
        isRegularAnswer() {
            return this.program.answer && this.program.answer.answerType === ANSWER_TYPE.REGULAR;
        },

        /**
         * 수신자가 예비인원으로 신청한 경우
         */
        isReservedAnswer() {
            return this.program.answer && this.program.answer.answerType === ANSWER_TYPE.RESERVED;
        },

        /**
         * 해당 프로그램을 포함하고 있는 수강신청
         */
        getParentQuestion() {
            return this.questions.find(question => question.questionNo === this.questionNo);
        },

        startGrade() {
            return this.filterInfo.type === FILTER_TYPE.RANGE && getFilterLabel(this.filterInfo.filters, this.program.filterTags[0]);
        },

        endGrade() {
            return this.filterInfo.type === FILTER_TYPE.RANGE && getFilterLabel(this.filterInfo.filters, this.program.filterTags[this.program.filterTags.length - 1]);
        },

        /**
         * 수신자가 해당 프로그램 신청버튼을 클릭하고 서버 응답을 대기하고 있는 경우
         */
        isLoading() {
            return this.getParentQuestion.programs.find(program => program.programNo === this.program.programNo).isLoading;
        },

        /**
         * 해당 Question의 현재 신청완료된 갯수 + 신청로딩중인 갯수 >= 최대선택 갯수인 경우
         */
        isMaxChoiceAnswer() {
            const question = this.getParentQuestion;
            if (question.maxChoice === 0) {
                return false;
            }

            const loadingProgramCount = question.programs.filter(program => program.isLoading).length;
            return question.choiceCount + loadingProgramCount >= question.maxChoice;
        },

        isDisabled() {
            return this.answerStateType === SURVEY_ANSWER_STATE_TYPE.DENY || this.isAfterEnrolmentPeriod;
        },

        showLoadingButton() {
            return (!this.isAfterEnrolmentPeriod || this.isFullAnswers) && this.isLoading;
        },

        showApplyButton() {
            return (!this.isAfterEnrolmentPeriod || this.isFullAnswers) && !this.isLoading && !this.hasAnswer;
        },

        showCancelButton() {
            return (!this.isAfterEnrolmentPeriod || this.isFullAnswers) && !this.isLoading && this.hasAnswer;
        },
    },

    methods: {
        ...mapActions({
            applyAnswerAction: APPLY_ANSWER,
            cancelAnswerAction: CANCEL_ANSWER,
            forceClose: FORCE_CLOSE,
        }),

        /**
         * 신청기간 이전 여부
         *     - 신청기간 이전에 페이지에 접속한 상태에서 시작시간이 되면 바로 값이 바뀌어야하기 때문에 getters로 사용하지 않음.
         * @returns {boolean}
         */
        isBeforeEnrolmentPeriod() {
            return Date.now() < toDateFromISOFormat(this.startDate);
        },

        /**
         * 신청하기
         */
        applyAnswer() {
            if (!this.validateApplyAnswer()) {
                return;
            }

            if (this.isMaxChoiceAnswer) {
                showToast(this.$t('EnrolmentResponseProgram_maxApply', {max: this.getParentQuestion.maxChoice}), this.isWebView);
                return;
            }

            this.applyAnswerAction({
                programNo: this.program.programNo,
            });
        },

        validateApplyAnswer() {
            if (this.isPreview) {
                return false;
            }

            //기간이 종료된 수강신청의 경우
            if (this.isAfterEnrolmentPeriod) {
                this.forceClose();
                return false;
            }

            //신청기간 이전인 경우
            if (this.isBeforeEnrolmentPeriod()) {
                this.showBeforeEnrolmentPeriodConfirm();
                return false;
            }

            return true;
        },

        /**
         * 신청 취소
         */
        cancelAnswer() {
            if (Date.now() > toDateFromISOFormat(this.endDate)) {
                this.forceClose();
                return;
            }

            if (this.hasAnswer) {
                this.showCancelConfirm();
            }
        },

        /**
         * 신청 취소 confirm 확인
         */
        confirmYes() {
            this.cancelAnswerAction({
                    surveyAnswerNo: this.program.answer.surveyAnswerNo,
                    programNo: this.program.programNo,
                })
                .then(() => {
                    showToast(this.$t('EnrolmentResponseProgram_enrolmentCanceled'), this.isWebView);
                });
        },

        /**
         * 응답상태가 신청안함 상태인데 프로그램을 클릭한 경우
         */
        checkTryAnswerWhenAnswerStateIsDenyAndValidateEnrolmentPeriod(event) {
            if (this.answerStateType !== SURVEY_ANSWER_STATE_TYPE.DENY) {
                return;
            }

            if (!this.validateApplyAnswer()) {
                return;
            }

            this.checkTryAnswerWhenAnswerStateIsDeny(event);
        },

        /**
         * 신청 취소 확인 팝업
         */
        showCancelConfirm() {
            this.$showConfirm({
                title: this.$t('EnrolmentResponseProgram_confirmCancel'),
                okText: this.$t('EnrolmentResponseProgram_doCancel'),
                cancelText: this.$t('common_no'),
                ok: async () => {
                    await this.confirmYes();
                },
            });
        },

        showBeforeEnrolmentPeriodConfirm() {
            this.$showAlert({
                title: this.$t('EnrolmentResponseProgram_noPeriod'),
                desc: this.$t('EnrolmentResponseProgram_schoolAgainInPeriod', {startDate: this.startDateString}),
                okText: this.$t('common_confirm'),
            });
        },
    },

    created() {
        getAssetInfo(this.$route.query.serviceId, PAGE_TYPE.RESPONSE)
            .assets
            .loading
            .then(path => {
                this.loadingSvgPath = path.default;
            });
    },

};
</script>

<style scoped lang="scss">
.desc_wrap {
  .desc_text {
    color: #666;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
  }
}
</style>
